import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableService,
} from '@web/shared/ui';
import { Route, RouteParam } from '@web/common';
import { AdminBrandListAction } from '../../enums/admin-brand-list-action';
import { AdminBrandListCustomCellTemplateComponent } from '../../components/admin-brand-list-custom-cell-template/admin-brand-list-custom-cell-template.component';
import { AdminBrandCollectionItem } from '../../models/admin-brand-collection-item';
import { AdminBrandCollectionFilter } from '../../models/admin-brand-collection-filter';
import { AdminBrandListItemAction } from '../../enums/admin-brand-list-item-action';
import { AdminBrandDataSourceService } from '../../services/admin-brand-data-source.service';
import { once } from '@web/utils';
import { AdminBrandStatusProviderService } from '../../providers/admin-brand-status-provider.service';
import { of } from 'rxjs';

@Component({
  selector: 'app-admin-brand-list',
  imports: [AdminBrandListCustomCellTemplateComponent, TableComponent],
  templateUrl: './admin-brand-list.component.html',
  styleUrl: './admin-brand-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBrandListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminBrandCollectionItem, AdminBrandCollectionFilter, AdminBrandListItemAction, never, AdminBrandListAction>).create();
  private readonly brandDataSource = inject(AdminBrandDataSourceService);
  private readonly table = inject(TableService);
  private readonly statusProvider = inject(AdminBrandStatusProviderService);

  public readonly createBrandVisibility = input<boolean>(false);

  protected config!: TableConfig<
    AdminBrandCollectionItem,
    AdminBrandCollectionFilter,
    AdminBrandListItemAction,
    never,
    AdminBrandListAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .dataSource({
        fetch: (lazyLoad) => this.brandDataSource.fetchCollection(lazyLoad),
      })
      .filters((builder) => [
        builder.initText('name').build(),
        builder.initText('slug').build(),
        builder
          .initSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),
        builder.initDateRange('createdAt').build(),
      ])
      .columns((builder) => [
        builder //
          .initCustom('logoUrl')
          .width('8rem')
          .label('Logotyp')
          .build(),

        builder //
          .initText('name')
          .filterKey('name')
          .sortKey('name')
          .label('Nazwa')
          .width('20rem')
          .build(),

        builder //
          .initDateTime('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .label('Data utworzenia')
          .build(),

        builder //
          .initCustom('status')
          .filterKey('status')
          .sortKey('status')
          .width('20rem')
          .label('Status')
          .build(),

        builder //
          .initCustom('homeUrl')
          .filterable(false)
          .sortable(false)
          .label('Adres domowy')
          .width('30rem')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(AdminBrandListAction.Create)
          .label('Dodaj markę')
          .visibility(() => of(this.createBrandVisibility()))
          .routerLink((provider) => provider.routerLink(Route.BrandCreate))
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminBrandListItemAction.Preview)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .visibility((item) => item.canBeViewed)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BrandDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initRouterLink(AdminBrandListItemAction.Edit)
          .icon(Icon.Pencil)
          .tooltip('Edytuj')
          .visibility((item) => item.canBeEdited)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BrandEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initCommand(AdminBrandListItemAction.Remove)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .visibility((item) => item.canBeRemoved)
          .command((item) =>
            this.brandDataSource
              .removeById(item.id)
              .pipe(once())
              .subscribe(() => this.refresh()),
          )
          .build(),
      ])
      .build();
  }

  private refresh(): void {
    this.table.refresh();
  }
}
